/**
 * Shown after an invitation is created: the link (always), a copy button, and a plain
 * statement of whether Nara sent it. The admin must never assume it was sent.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, Copy, TriangleAlert } from "lucide-react";
import type { CreateInviteResponse } from "../../services/naraDoor";

interface InviteLinkResultProps {
  invite: CreateInviteResponse;
  onDone: () => void;
}

export function InviteLinkResult({ invite, onDone }: InviteLinkResultProps) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const handleCopy = async () => {
    setCopyError(false);
    try {
      await navigator.clipboard.writeText(invite.link);
      setCopied(true);
    } catch (err) {
      console.error("Invite link copy failed", (err as Error).name);
      setCopyError(true);
    }
  };

  const sent = invite.sentVia !== null;

  return (
    <div className="mt-4 space-y-4">
      <div
        className={`p-3 rounded-lg border text-sm flex items-start gap-2 ${
          sent
            ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
            : "bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300"
        }`}
      >
        {sent ? <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" /> : <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />}
        {sent ? t(`invite.result.sent.${invite.sentVia}`) : t("invite.result.notSent")}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">{t("invite.result.linkLabel")}</label>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={invite.link}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-lg bg-muted text-sm"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-accent transition-colors text-sm"
          >
            <Copy className="h-4 w-4" />
            {copied ? t("invite.result.copied") : t("invite.result.copy")}
          </button>
        </div>
        {copyError && <p className="mt-1 text-xs text-red-600">{t("invite.result.copyFailed")}</p>}
        <p className="mt-1 text-xs text-muted-foreground">
          {t("invite.result.expires", { date: new Date(invite.expiresAt).toLocaleDateString(i18n.language) })}
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onDone}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t("invite.result.done")}
        </button>
      </div>
    </div>
  );
}
